import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 20000,
});

// Global 401 handler for the "session_replaced" reason. When another device
// signs into the same account, our token's `sid` no longer matches the user's
// active session, so every subsequent API call returns 401 with this detail.
// We drop the user back to the login screen with a clear message so they know
// why they got booted (rather than a mysterious "session expired").
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && err?.response?.data?.detail === "session_replaced") {
      try { sessionStorage.setItem("psa.kicked_reason", "session_replaced"); } catch { /* noop */ }
      // Avoid infinite loops if we're already on /login
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login?kicked=1");
      }
    }
    return Promise.reject(err);
  },
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Default fallback lists; backend overrides via /api/settings/*
export const DEFAULT_PRIORITIES = [
  { id: "high", label: "HIGH", color: "#EE352E" },
  { id: "medium", label: "MEDIUM", color: "#FF8800" },
  { id: "low", label: "LOW", color: "#2E86DE" },
];

export const DEFAULT_BRANCHES = [
  { id: "FIRE", label: "Fire", color: "#EE352E", icon: "flame" },
  { id: "EMS", label: "EMS", color: "#00B894", icon: "ambulance" },
  { id: "PD", label: "Police", color: "#0984E3", icon: "police" },
  { id: "WEATHER", label: "Weather", color: "#FDCB6E", icon: "cloud-rain" },
];

export const STATUSES = [
  { value: "pending", label: "Pending", color: "#FF8800" },
  { value: "dispatched", label: "Dispatched", color: "#2E86DE" },
  { value: "on-scene", label: "On Scene", color: "#22c55e" },
  { value: "cleared", label: "Cleared", color: "#888888" },
];

export function priorityColor(id, priorities = DEFAULT_PRIORITIES) {
  // Legacy "critical" maps to HIGH red
  const key = id === "critical" ? "high" : id;
  const found = priorities.find((p) => p.id === key);
  return found ? found.color : "#888";
}

export function branchInfo(id, branches = DEFAULT_BRANCHES) {
  const upper = (id || "PD").toUpperCase();
  return branches.find((b) => b.id === upper) || branches[2];
}

export function statusColor(s) {
  return (STATUSES.find((x) => x.value === s) || STATUSES[0]).color;
}
